import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Clock, Mail, Phone, ArrowLeft, AlertCircle, Building2, User } from 'lucide-react';
import { cn } from '../../lib/utils';

const PendingApprovalPage = () => {
  const { user, logout } = useAuth();

  const isAffiliate = user?.team_member_type === 'affiliate';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden p-4">
      {/* Background blur effect */}
      <div className="absolute inset-0 backdrop-blur-sm">
        <div className="absolute top-20 left-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-lg relative z-10 shadow-2xl border-2 border-amber-200/50">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-4 animate-pulse">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Compte en attente de validation
          </CardTitle>
          <CardDescription className="text-slate-600 mt-2">
            {isAffiliate 
              ? "Votre demande pour devenir apporteur d'affaires est en cours d'examen"
              : "Votre demande d'ouverture de compte est en cours d'examen"
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* User Info */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className={cn(
                "w-14 h-14 rounded-full flex items-center justify-center text-white font-bold",
                isAffiliate ? "bg-gradient-to-br from-orange-500 to-amber-600" : "bg-gradient-to-br from-cyan-500 to-blue-600"
              )}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div>
                <p className="font-semibold text-slate-800">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                {user?.company_name && (
                  <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                    <Building2 className="w-3 h-3" />
                    {user.company_name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Message */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  L'agence doit approuver votre demande
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {isAffiliate 
                    ? "Un entretien téléphonique sera programmé pour finaliser votre candidature. Vous recevrez un email avec les prochaines étapes."
                    : "Notre équipe examine actuellement votre dossier. Vous recevrez un email de confirmation dès que votre compte sera activé."
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-600 mb-3">
              Des questions ? Contactez-nous :
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href="mailto:contact@agence-msc.fr"
                className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                contact@agence-msc.fr
              </a>
              <a 
                href="tel:0484492700"
                className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 transition-colors"
              >
                <Phone className="w-4 h-4" />
                04 84 49 27 00
              </a>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="outline"
              onClick={logout}
              className="flex-1 rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Se déconnecter
            </Button>
            <Link to="/site" className="flex-1">
              <Button className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                Visiter le site
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingApprovalPage;
